import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Save, RefreshCw, Copy, ExternalLink, Eye, EyeOff,
  Link2, Image, Code, ChevronDown, ChevronUp,
  Edit2, Users, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Use the preview/current URL as default since production domain may not be deployed yet
const CURRENT_DOMAIN = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_DOMAIN = process.env.REACT_APP_PUBLIC_DOMAIN || 'https://www.worldnuclearcongress.com';

const AdminSpeakerInvite = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    prestige: false,
    distinct: false,
    program: false,
    participants: false,
    urgency: false,
    buttons: false,
    honor: false,
    graphic: false
  });
  const [embedHtml, setEmbedHtml] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/speaker-invite`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const seedContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/speaker-invite/seed`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchContent();
      }
    } catch (err) {
      toast.error('Failed to seed content');
    }
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/speaker-invite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        toast.success('Content saved successfully');
        fetchContent();
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const regenerateToken = async () => {
    if (!window.confirm('Are you sure? The old link will stop working.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/speaker-invite/regenerate-token`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success('Token regenerated');
        setContent({ ...content, access_token: result.access_token });
      }
    } catch (err) {
      toast.error('Failed to regenerate token');
    }
  };

  const toggleActive = async () => {
    try {
      const response = await fetch(`${API_URL}/api/speaker-invite/toggle-active`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setContent({ ...content, is_active: result.is_active });
      }
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const copyInviteLink = () => {
    // Copy the working link (current domain) by default
    const link = `${CURRENT_DOMAIN}/speaker-invite/${content.access_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Working invite link copied to clipboard!');
  };
  
  const copyProductionLink = () => {
    // Copy the production domain link (for when deployed)
    const link = `${PRODUCTION_DOMAIN}/speaker-invite/${content.access_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Production link copied (use after deploying to worldnuclearcongress.com)');
  };

  const fetchEmbedHtml = async () => {
    try {
      const response = await fetch(`${API_URL}/api/speaker-invite/graphic-html`);
      if (response.ok) {
        const data = await response.json();
        setEmbedHtml(data);
      }
    } catch (err) {
      toast.error('Failed to fetch embed code');
    }
  };

  const copyEmbedHtml = () => {
    if (embedHtml) {
      navigator.clipboard.writeText(embedHtml.html);
      toast.success('Embed HTML copied to clipboard');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-[#00457f]" />
        <h3 className="font-bold text-gray-900">{title}</h3>
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="w-12 h-12 border-4 border-[#00457f] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!content || !content.id) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Speaker Invite Page Not Initialized</h2>
            <p className="text-gray-600 mb-6">Click the button below to create the speaker invite page with content from the WNC Overview document.</p>
            <Button onClick={seedContent} className="bg-[#00457f] hover:bg-[#003461]">
              <RefreshCw className="w-4 h-4 mr-2" />
              Initialize Speaker Invite Page
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Speaker Invite Page Manager</h1>
            <p className="text-gray-600">Manage the private speaker invitation page</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={toggleActive}
              variant={content.is_active ? "outline" : "default"}
              className={content.is_active ? "border-green-500 text-green-600" : "bg-red-500 hover:bg-red-600"}
            >
              {content.is_active ? (
                <><Eye className="w-4 h-4 mr-2" /> Page Active</>
              ) : (
                <><EyeOff className="w-4 h-4 mr-2" /> Page Inactive</>
              )}
            </Button>
            <Button onClick={saveContent} disabled={saving} className="bg-[#00457f] hover:bg-[#003461]">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="font-bold text-gray-900 mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button onClick={copyInviteLink} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2">
              <Copy className="w-4 h-4" />
              Copy Working Link
            </Button>
            <Button onClick={copyProductionLink} variant="outline" className="flex items-center gap-2">
              <Copy className="w-4 h-4" />
              Copy Production Link
            </Button>
            <Button onClick={regenerateToken} variant="outline" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Regenerate Link
            </Button>
            <a
              href={`${CURRENT_DOMAIN}/speaker-invite/${content.access_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Open Page
            </a>
            <Button onClick={fetchEmbedHtml} variant="outline" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Get Email Embed Code
            </Button>
          </div>
          
          {embedHtml && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Email Embed HTML</span>
                <Button onClick={copyEmbedHtml} size="sm" variant="outline">
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                {embedHtml.html}
              </pre>
              <div className="mt-3">
                <a
                  href={embedHtml.graphic_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#00457f] hover:underline flex items-center gap-1"
                >
                  <Image className="w-4 h-4" />
                  Preview Graphic
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Current Link Display */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-green-800 mb-2">
            <Link2 className="w-5 h-5" />
            <span className="font-medium">Working Invite Link</span>
            <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">USE THIS</span>
          </div>
          <code className="text-sm text-green-900 break-all block">
            {CURRENT_DOMAIN}/speaker-invite/{content.access_token}
          </code>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Link2 className="w-5 h-5" />
            <span className="font-medium">Production Link</span>
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">After Deployment</span>
          </div>
          <code className="text-sm text-gray-600 break-all block">
            {PRODUCTION_DOMAIN}/speaker-invite/{content.access_token}
          </code>
          <p className="text-xs text-gray-500 mt-2">This link will work after deploying to worldnuclearcongress.com</p>
        </div>

        {/* Content Sections */}
        <div className="space-y-4">
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Hero Section" section="hero" icon={Edit2} />
            {expandedSections.hero && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={content.hero_title}
                    onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={content.hero_subtitle}
                    onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supporting Line</label>
                  <input
                    type="text"
                    value={content.hero_supporting_line || ''}
                    onChange={(e) => setContent({ ...content, hero_supporting_line: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Prestige Statement */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Prestige Statement" section="prestige" icon={Edit2} />
            {expandedSections.prestige && (
              <div className="p-4">
                <textarea
                  value={content.prestige_statement}
                  onChange={(e) => setContent({ ...content, prestige_statement: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                />
              </div>
            )}
          </div>

          {/* What Makes Distinct */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="What Makes Distinct Section" section="distinct" icon={Edit2} />
            {expandedSections.distinct && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input
                    type="text"
                    value={content.distinct_title}
                    onChange={(e) => setContent({ ...content, distinct_title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Introduction</label>
                  <textarea
                    value={content.distinct_intro}
                    onChange={(e) => setContent({ ...content, distinct_intro: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Points (one per line)</label>
                  <textarea
                    value={content.distinct_points?.join('\n') || ''}
                    onChange={(e) => setContent({ ...content, distinct_points: e.target.value.split('\n').filter(Boolean) })}
                    rows={8}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Closing Statement</label>
                  <textarea
                    value={content.distinct_closing || ''}
                    onChange={(e) => setContent({ ...content, distinct_closing: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Program Highlights */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Program Highlights" section="program" icon={Edit2} />
            {expandedSections.program && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <input
                    type="text"
                    value={content.program_title}
                    onChange={(e) => setContent({ ...content, program_title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Highlights (one per line)</label>
                  <textarea
                    value={content.program_highlights?.join('\n') || ''}
                    onChange={(e) => setContent({ ...content, program_highlights: e.target.value.split('\n').filter(Boolean) })}
                    rows={10}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Participants */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Participants Section" section="participants" icon={Users} />
            {expandedSections.participants && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={content.participants_title}
                      onChange={(e) => setContent({ ...content, participants_title: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={content.participants_subtitle}
                      onChange={(e) => setContent({ ...content, participants_subtitle: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                </div>
                
                {/* Notice about linked speakers */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">Participants Linked to Speakers Database</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        The participants displayed on the Speaker Invite page are automatically pulled from the main Speakers database. 
                        Any speakers you add or update there will appear here in alphabetical order.
                      </p>
                      <button
                        onClick={() => navigate('/admin/speakers')}
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        Manage Speakers
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Urgency & Buttons */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Urgency Message & Action Buttons" section="urgency" icon={Edit2} />
            {expandedSections.urgency && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Message</label>
                  <textarea
                    value={content.urgency_message}
                    onChange={(e) => setContent({ ...content, urgency_message: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Program Button Text</label>
                    <input
                      type="text"
                      value={content.program_button_text}
                      onChange={(e) => setContent({ ...content, program_button_text: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Program Button Link</label>
                    <input
                      type="text"
                      value={content.program_button_link}
                      onChange={(e) => setContent({ ...content, program_button_link: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Register Button Text</label>
                    <input
                      type="text"
                      value={content.register_button_text}
                      onChange={(e) => setContent({ ...content, register_button_text: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Register Button Link</label>
                    <input
                      type="text"
                      value={content.register_button_link}
                      onChange={(e) => setContent({ ...content, register_button_link: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Honor Statement */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Honor Statement (Closing)" section="honor" icon={Edit2} />
            {expandedSections.honor && (
              <div className="p-4">
                <textarea
                  value={content.honor_statement}
                  onChange={(e) => setContent({ ...content, honor_statement: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                />
              </div>
            )}
          </div>

          {/* Email Graphic Settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <SectionHeader title="Email Graphic Settings" section="graphic" icon={Image} />
            {expandedSections.graphic && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Graphic Title</label>
                    <input
                      type="text"
                      value={content.graphic_title}
                      onChange={(e) => setContent({ ...content, graphic_title: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Graphic Subtitle</label>
                    <input
                      type="text"
                      value={content.graphic_subtitle}
                      onChange={(e) => setContent({ ...content, graphic_subtitle: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Display</label>
                    <input
                      type="text"
                      value={content.graphic_date}
                      onChange={(e) => setContent({ ...content, graphic_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Display</label>
                    <input
                      type="text"
                      value={content.graphic_location}
                      onChange={(e) => setContent({ ...content, graphic_location: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                    <input
                      type="text"
                      value={content.graphic_cta}
                      onChange={(e) => setContent({ ...content, graphic_cta: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={content.graphic_background_color}
                        onChange={(e) => setContent({ ...content, graphic_background_color: e.target.value })}
                        className="w-12 h-10 border rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={content.graphic_background_color}
                        onChange={(e) => setContent({ ...content, graphic_background_color: e.target.value })}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Graphic Preview */}
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Graphic Preview</p>
                  <img 
                    src={`${API_URL}/api/speaker-invite/graphic/${content.access_token}`}
                    alt="Email Graphic Preview"
                    className="max-w-full h-auto rounded shadow"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button (Sticky) */}
        <div className="fixed bottom-6 right-6">
          <Button 
            onClick={saveContent} 
            disabled={saving}
            size="lg"
            className="bg-[#00457f] hover:bg-[#003461] shadow-xl"
          >
            {saving ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
            Save All Changes
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSpeakerInvite;
